import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  DEFAULT_COMPANY_SETTINGS,
  type CompanySettings,
  type AccountDto,
  type CreateSupplierPaymentRequest,
  type SupplierDto,
  type SupplierPaymentDto,
  type PurchaseBillSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import WithholdingPayableReport from './WithholdingPayableReport';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canRecord: boolean;
  onSessionLost: () => void;
}

const CASH_SUBLEDGERS = new Set(['cashbox', 'bank_or_wallet']);

const ISSUE_LABELS: Record<string, string> = {
  ALLOCATION_EXCEEDS_OPEN: 'المخصص أكبر من المتبقى على الفاتورة',
  ALLOCATION_EXCEEDS_AMOUNT: 'مجموع التخصيص أكبر من مبلغ السداد',
  ALLOCATION_WRONG_PARTY: 'الفاتورة لمورد آخر',
  ALLOCATION_DOCUMENT_UNKNOWN: 'الفاتورة غير موجودة',
  ALLOCATION_DUPLICATE_DOCUMENT: 'الفاتورة مكررة فى التخصيص',
  ALLOCATION_NOT_POSITIVE: 'المخصص يجب أن يكون موجبًا',
  PAYMENT_SOURCE_ACCOUNT: 'حساب الصرف يجب أن يكون خزينة أو بنكًا',
  CASHBOX_NEGATIVE: 'رصيد الخزينة لا يكفى',
  CASHBOX_NOT_CONFIGURED: 'لا خزينة معرّفة على هذا الحساب',
  SUPPLIER_NOT_FOUND: 'المورد غير موجود',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية',
  WITHHOLDING_DISABLED: 'الخصم من المنبع على السداد مطفأ فى الإعدادات',
  WITHHOLDING_AMOUNT_MISMATCH: 'مبلغ الخصم لا يساوى الوعاء × النسبة',
  WITHHOLDING_INPUT_MISSING: 'الخصم يحتاج مبلغًا، أو نسبة مع وعائها',
  WITHHOLDING_NOT_POSITIVE: 'الخصم يجب أن يكون موجبًا',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/**
 * السداد للموردين: مبلغ يُصرف من خزينة أو بنك ويُخصص على فواتير المورد المفتوحة. ما لم
 * يُخصص يبقى مدينًا على المورد. «الأقدم أولاً» اقتراح فقط — المستخدم يراه ويعدّله.
 */
export default function SupplierPayments({
  companyId,
  currency,
  accounts,
  canRecord,
  onSessionLost,
}: Props) {
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [payments, setPayments] = useState<SupplierPaymentDto[] | null>(null);
  const [supplierId, setSupplierId] = useState('');
  const [openInvoices, setOpenInvoices] = useState<PurchaseBillSummary[]>([]);
  const [paymentDate, setPaymentDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [sourceAccountCode, setSourceAccountCode] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<SupplierPaymentDto | null>(null);
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [whRate, setWhRate] = useState('');
  const [whBase, setWhBase] = useState('');
  const [whAmount, setWhAmount] = useState('');

  const cashAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          account.subledger !== null &&
          CASH_SUBLEDGERS.has(account.subledger),
      ),
    [accounts],
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        setIssues(caught.problem.issues ?? []);
        setFailure(
          caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    Promise.all([api.suppliers(companyId), api.supplierPayments(companyId)])
      .then(([supplierList, paymentList]) => {
        setSuppliers(supplierList);
        setPayments(paymentList);
      })
      .catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  // الإعدادات تحكم ظهور الخصم من المنبع؛ تعذّر قراءتها يُبقى الافتراضى (مطفأ)
  useEffect(() => {
    api
      .companySettings(companyId)
      .then((response) => setSettings(response.settings))
      .catch(() => setSettings(DEFAULT_COMPANY_SETTINGS));
  }, [companyId]);
  const withholdingEnabled = settings.tax.withholding.onPayments;

  useEffect(() => {
    if (!supplierId) {
      setOpenInvoices([]);
      return;
    }
    api
      .purchaseBills(companyId, supplierId, true)
      .then((list) =>
        // الأقدم استحقاقًا أولاً — ترتيب العرض والاقتراح
        setOpenInvoices([...list].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))),
      )
      .catch(handleError);
  }, [companyId, supplierId, handleError, recorded]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    setRecorded(null);
  }

  const parsedAmount = parseAmount(amount);
  const allocationValues = openInvoices.map((invoice) =>
    parseAmount(allocations[invoice.id] ?? ''),
  );
  const allocated = allocationValues.every((value) => value !== null)
    ? Money.sum(allocationValues as string[])
    : null;
  // الخصم: نسبة على وعاء تُحسب بـ Money، أو مبلغ يُكتب. الخادم يرفض إن اختلفا
  const rate = withholdingEnabled ? parseAmount(whRate) : '0';
  const base = withholdingEnabled ? parseAmount(whBase) : '0';
  const typedWithholding = withholdingEnabled ? parseAmount(whAmount) : '0';
  const byRate =
    rate !== null && base !== null && Money.of(rate).isPositive() && Money.of(base).isPositive();
  const typedPositive = typedWithholding !== null && Money.of(typedWithholding).isPositive();
  const withholding = typedPositive
    ? Money.of(typedWithholding)
    : byRate
      ? Money.of(base).mul(rate).div('100').round(2)
      : Money.zero();
  const withholdingValid = rate !== null && base !== null && typedWithholding !== null;
  // ما يسدده السند من ذمة المورد: النقد مع الخصم
  const settled =
    parsedAmount !== null && withholdingValid ? Money.of(parsedAmount).add(withholding) : null;
  const unallocated = settled !== null && allocated !== null ? settled.sub(allocated) : null;
  const ready =
    supplierId !== '' &&
    sourceAccountCode !== '' &&
    parsedAmount !== null &&
    Money.of(parsedAmount).isPositive() &&
    unallocated !== null &&
    !unallocated.isNegative();

  function suggestOldestFirst(): void {
    if (settled === null) return;
    let remaining = settled;
    const next: Record<string, string> = {};
    for (const invoice of openInvoices) {
      if (!remaining.isPositive()) break;
      const open = Money.of(invoice.openBalance);
      const take = remaining.lt(open) ? remaining : open;
      next[invoice.id] = take.toFixed(2);
      remaining = remaining.sub(take);
    }
    setAllocations(next);
    touched();
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || parsedAmount === null) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const chosen = openInvoices
      .map((invoice, index) => ({ billId: invoice.id, amount: allocationValues[index] ?? null }))
      .filter(
        (item): item is { billId: string; amount: string } =>
          item.amount !== null && Money.of(item.amount).isPositive(),
      );
    const body: CreateSupplierPaymentRequest = {
      supplierId,
      paymentDate,
      amount: parsedAmount,
      sourceAccountCode,
      ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
      allocations: chosen,
      ...(withholding.isPositive()
        ? {
            withholding: {
              ...(byRate && rate !== null && base !== null ? { ratePercent: rate, base } : {}),
              ...(typedPositive && typedWithholding !== null ? { amount: typedWithholding } : {}),
            },
          }
        : {}),
    };
    try {
      const payment = await api.createSupplierPayment(companyId, idempotencyKey, body);
      setRecorded(payment);
      setAmount('');
      setExternalReference('');
      setAllocations({});
      setWhRate('');
      setWhBase('');
      setWhAmount('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const chosenIndexes = openInvoices
    .map((invoice, index) => ({ index, value: allocationValues[index] ?? null }))
    .filter((item) => item.value !== null && Money.of(item.value).isPositive())
    .map((item) => item.index);
  // فهرس التخصيص فى الطلب ≠ فهرس الصف: الطلب يحمل المخصص الموجب فقط
  const issuesOfRow = (rowIndex: number) =>
    issues.filter((issue) => issue.lineIndex === chosenIndexes.indexOf(rowIndex));
  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  return (
    <>
      {canRecord && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>سداد لمورد</h2>
          <div className="form-row">
            <label className="grow">
              المورد
              <select
                value={supplierId}
                onChange={(event) => {
                  setSupplierId(event.target.value);
                  setAllocations({});
                  touched();
                }}
              >
                <option value="">— اختر موردًا —</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.code} — {supplier.nameAr}
                  </option>
                ))}
              </select>
              <Hint>المورد الذى دفعت له.</Hint>
            </label>
            <label>
              التاريخ
              <input
                type="date"
                value={paymentDate}
                onChange={(event) => {
                  setPaymentDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label>
              المبلغ
              <input
                inputMode="decimal"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  touched();
                }}
              />
              <Hint>المبلغ الذى خرج فعلاً من الخزينة أو البنك.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label className="grow">
              دُفع من
              <select
                value={sourceAccountCode}
                onChange={(event) => {
                  setSourceAccountCode(event.target.value);
                  touched();
                }}
              >
                <option value="">— خزينة أو بنك —</option>
                {cashAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>الخزينة أو البنك الذى خرجت منه الفلوس.</Hint>
            </label>
            <label className="grow">
              مرجع (شيك/تحويل)
              <input
                value={externalReference}
                maxLength={64}
                onChange={(event) => {
                  setExternalReference(event.target.value);
                  touched();
                }}
              />
              <Hint>رقم الشيك أو التحويل البنكى، ليسهل الرجوع إليه عند المراجعة.</Hint>
            </label>
          </div>

          {withholdingEnabled && (
            <div className="form-row">
              <label>
                نسبة الخصم من المنبع %
                <input
                  inputMode="decimal"
                  list="payment-withholding-rates"
                  value={whRate}
                  placeholder="اختيارى"
                  onChange={(event) => {
                    setWhRate(event.target.value);
                    touched();
                  }}
                />
                <datalist id="payment-withholding-rates">
                  {settings.tax.withholding.paymentRates.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
                <Hint>
                  لو تخصم جزءًا من مستحق المورد وتورّده أنت للضرائب، اكتب النسبة هنا. اسأل محاسبك عن
                  النسبة الصحيحة.
                </Hint>
              </label>
              <label>
                الوعاء
                <input
                  inputMode="decimal"
                  value={whBase}
                  placeholder="المبلغ الخاضع"
                  onChange={(event) => {
                    setWhBase(event.target.value);
                    touched();
                  }}
                />
                <Hint>المبلغ الذى تُحسب عليه النسبة.</Hint>
              </label>
              <label>
                مبلغ الخصم
                <input
                  inputMode="decimal"
                  value={whAmount}
                  placeholder={byRate ? withholding.toFixed(2) : 'أو اكتبه مباشرة'}
                  onChange={(event) => {
                    setWhAmount(event.target.value);
                    touched();
                  }}
                />
                <Hint>يُحسب تلقائيًا، ويُسجَّل عليك لتورّده للضرائب لاحقًا.</Hint>
              </label>
              <span className="label grow">
                ما تخصمه من المورد لحساب المصلحة يُسدد فاتورته كالنقد، ويُسجل التزامًا عليك حتى
                تورّده. النسب القانونية يحددها مستشار الضرائب — أضفها فى الإعدادات.
              </span>
            </div>
          )}

          {supplierId && (
            <>
              <div className="panel-head">
                <h3>الفواتير المفتوحة</h3>
                {openInvoices.length > 0 && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={suggestOldestFirst}
                    disabled={settled === null}
                  >
                    اقترح التوزيع: الأقدم أولاً
                  </button>
                )}
              </div>
              {openInvoices.length === 0 ? (
                <p className="sub">
                  لا فواتير مفتوحة — السداد كله يبقى مدينًا على المورد تحت الحساب.
                </p>
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>الفاتورة</th>
                        <th>الاستحقاق</th>
                        <th>الإجمالى</th>
                        <th>المتبقى</th>
                        <th>يُخصص</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openInvoices.map((invoice, index) => (
                        <tr key={invoice.id}>
                          <td className="mono">{invoice.number}</td>
                          <td>{invoice.dueDate}</td>
                          <td className="num-cell">{formatMoney(invoice.total)}</td>
                          <td className="num-cell">{formatMoney(invoice.openBalance)}</td>
                          <td>
                            <input
                              inputMode="decimal"
                              value={allocations[invoice.id] ?? ''}
                              onChange={(event) => {
                                setAllocations((current) => ({
                                  ...current,
                                  [invoice.id]: event.target.value,
                                }));
                                touched();
                              }}
                            />
                            {issuesOfRow(index).map((issue, i) => (
                              <p key={i} className="error">
                                {ISSUE_LABELS[issue.code] ?? issue.message}
                              </p>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="sub">
                {withholding.isPositive() &&
                  'يسدد ' +
                    (settled ? formatMoney(settled) : '—') +
                    ' (نقد + خصم ' +
                    formatMoney(withholding) +
                    ') • '}
                المخصص {allocated ? formatMoney(allocated) : '—'} • يبقى تحت الحساب{' '}
                {unallocated ? formatMoney(unallocated) : '—'} {currency}
                {unallocated?.isNegative() ? ' — التخصيص أكبر من المبلغ' : ''}
              </p>
            </>
          )}

          {generalIssues.map((issue, i) => (
            <p key={i} className="error">
              {ISSUE_LABELS[issue.code] ?? issue.message}
            </p>
          ))}
          {failure && <p className="error">{failure}</p>}
          {recorded && (
            <p className="sub">
              سُجل السداد {recorded.number} بمبلغ {formatMoney(recorded.amount)}
              {Money.of(recorded.withholdingAmount).isPositive() &&
                ' وخصم منبع ' + formatMoney(recorded.withholdingAmount)}{' '}
              — قيده {recorded.journalNumber}، والباقى تحت الحساب{' '}
              {formatMoney(recorded.unallocated)}.
            </p>
          )}
          <div className="form-actions sticky">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى التسجيل…' : 'تسجيل السداد'}
            </button>
            <Hint block>
              يُنقص المبلغ مما عليك للمورد ويخصمه من الخزينة أو البنك. ليس مصروفًا جديدًا — المصروف
              سُجّل يوم فاتورة المورد. ولا تصرف خزينة أكثر من رصيدها. بعد الترحيل لا يُعدَّل ولا
              يُحذف — الخطأ يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>السدادات</h2>
        {payments === null && !failure && <Loading />}
        {payments?.length === 0 && <p className="sub">لا سدادات بعد.</p>}
        {payments && payments.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>المورد</th>
                  <th>التاريخ</th>
                  <th>المبلغ</th>
                  <th>خصم المنبع</th>
                  <th>خُصص على</th>
                  <th>تحت الحساب</th>
                  <th>القيد</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="mono">{payment.number}</td>
                    <td>{payment.supplierName}</td>
                    <td>{payment.paymentDate}</td>
                    <td className="num-cell">{formatMoney(payment.amount)}</td>
                    <td className="num-cell">{formatMoney(payment.withholdingAmount)}</td>
                    <td className="mono">
                      {payment.allocations
                        .map((item) => item.billNumber + ': ' + formatMoney(item.amount))
                        .join('، ')}
                    </td>
                    <td className="num-cell">{formatMoney(payment.unallocated)}</td>
                    <td className="mono">{payment.journalNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* التقرير يبقى ظاهرًا ولو أُطفئ الخصم: ما خُصم قبل الإطفاء ما زال مستحقًا للتوريد */}
      <WithholdingPayableReport
        companyId={companyId}
        currency={currency}
        defaultFrom={today().slice(0, 8) + '01'}
        defaultTo={today()}
        onSessionLost={onSessionLost}
      />
    </>
  );
}
